import React, { useMemo } from "react";
import { useIntl } from "umi";
import { TFrameType } from "./_type";
import { LANGUAGE_EN } from "@home/_config/config";
import { renderGenreSubGenre } from "@/pages/benchmark/components/GenreFilter/renderGenreSubGenre";
import CountryFilter from "@/pages/benchmark/components/CountryFilter";
import { RangeDateFilter } from "@/pages/benchmark/components/RangeDateFilter";
import OptionItemInfo from "@/pages/dashboard/components/FilterSelect/components/OptionItemInfo";
import FilterSelectButton from "@/pages/dashboard/components/FilterSelect/components/FilterSelectButton";

export const useFilterList = (frameType: TFrameType) => {
  const { formatMessage, locale } = useIntl(); // 中英切换方法
  const isEN = locale === LANGUAGE_EN;
  const { filterConfigList } = useMemo(() => {

    const allRadioContent = {
      value: 'all',
      text: formatMessage({ id: 'dbb_filter_all' }),
    };

    const radioListContent = [
      {
        value: 'percent',
        text: formatMessage({ id: 'dbb_filter_download_by_percentile' }),
        startText: formatMessage({ id: 'dbb_filter_download_by_percentile_top' }),
        endText: formatMessage({ id: 'dbb_filter_download_by_percentile_of_games' }),
        type: "input",
        errorReg: /^[1-9]\d*$/,
        errorMsg: formatMessage({ id: 'dbb_filter_input_error' }),
        // selectedText: formatMessage({ id: 'dbb_filter_download_by_percentile' }),
        formatter: 'percentInt',
        default: {
          radioSelectedValue: 'percent',
          inputValue: {
            max: '10',
            min: '',
          }
        },
      },
      {
        value: 'number',
        text: formatMessage({ id: 'dbb_filter_download_number_og_top10' }),
        startText: formatMessage({ id: 'dbb_filter_download_by_top_game' }),
        endText: formatMessage({ id: 'dbb_filter_download_by_top_game_of_games' }),
        // selectedText: formatMessage({ id: 'dbb_filter_download_number_og_top10' }),
        type: "input",
        formatter: 'intBmk00',
        errorReg: /^[1-9]\d*$/,
        errorMsg: formatMessage({ id: 'dbb_filter_input_error' }),
        default: {
          radioSelectedValue: 'number',
          inputValue: {
            max: '100',
            min: '',
          }
        },
      },
      {
        value: 'custom',
        text: formatMessage({ id: 'dbb_filter_download_customized_range' }),
        type: "customInput",
        formatter: 'intBmk00',
        // eslint-disable-next-line no-useless-escape
        errorReg: /^[0-9]*[\.]?[0-9]*$/,
        errorMsg: formatMessage({ id: 'dbb_filter_input_error' }),
        // selectedText: formatMessage({ id: 'dbb_filter_download_customized_range' }),
        // default: {
        //   radioSelectedValue: 'custom',
        //   inputValue: {
        //     max: '500',
        //     min: '233',
        //   }
        // },
      }
    ];

    const downLoadRadioListContent = [
      {
        value: 'percent',
        text: formatMessage({ id: 'dbb_filter_download_by_percentile' }),
        startText: formatMessage({ id: 'dbb_filter_download_by_percentile_top' }),
        endText: formatMessage({ id: 'dbb_filter_download_by_percentile_of_games' }),
        type: "input",
        formatter: 'percentInt',
        errorReg: /^[1-9]\d*$/,
        errorMsg: 'dbb_filter_input_error',
        default: {
          radioSelectedValue: 'percent',
          inputValue: {
            max: '10',
            min: '',
          }
        },
      },
      {
        value: 'number',
        text: formatMessage({ id: 'dbb_filter_download_number_og_top10' }),
        startText: formatMessage({ id: 'dbb_filter_download_by_top_game' }),
        endText: formatMessage({ id: 'dbb_filter_download_by_top_game_of_games' }),
        type: "input",
        formatter: 'intBmk00',
        errorReg: /^[1-9]\d*$/,
        errorMsg: 'dbb_filter_input_error',
        default: {
          radioSelectedValue: 'number',
          inputValue: {
            max: '100',
            min: '',
          }
        },
      },
      {
        value: 'custom',
        text: formatMessage({ id: 'dbb_filter_download_customized_range' }),
        type: "customInput",
        formatter: 'intBmk00',
        errorReg: /^\d*$/,
        errorMsg: 'dbb_filter_input_error',
      }
    ];

    // 筛选器名称映射，和后端核对后，后端暂不支持名称配置话，前端写死
    const FILTER_MOBILE_NAME_MAP = {
      'genre_subgenre': {
        nameEN: 'Genre/Game',
        nameCN: '品类/游戏',
        // metrics: { type: '', regions: [], markets: [] },
        default: {
          genre: [],
          subgenre: ['255'],
          type: 'subgenre',
        },
        view_type: 'cascade',
        visible: 1,
        render: renderGenreSubGenre
      },
      'game': {
        nameEN: 'Game',
        nameCN: '游戏',
        default: ['255'],
        view_type: 'multiple',
        visible: 1,
        radioOptions: [{
          value: '255',
          text: formatMessage({ id: 'dbb_filter_all' }),
          info: ''
        }],
      },
      'country': {
        nameEN: 'Region/Country',
        nameCN: '区域/国家',
        default: 'global',
        view_type: 'singleCascade',
        visible: 1,
        tooltipEn: 'Please enter country',
        tooltipCn: '请输入国家',
        render: (props: any) => <CountryFilter {...props} filterKey={props.key} />,
      },
      'platform': {
        nameEN: 'Platform',
        nameCN: '平台',
        default: 'Mobile',
        view_type: 'single',
        visible: 1,
        radioOptions: [{
          value: 'Mobile',
          text: formatMessage({ id: 'dbb_filter_all' }),
          info: formatMessage({ id: 'dbb_filter_all_info' }, { name: isEN ? 'Platform' : '平台' }),
        }]
      },
      'pcu': {
        dataKey: 'pcu',
        key: 'pcu',
        nameEN: 'PCU Range',
        nameCN: 'PCU 区间',
        name: formatMessage({ id: 'dbb_filter_pcu_title' }),
        customRangeUnitText: '-',
        customRangeLeftPlaceholder: formatMessage({ id: 'dbb_filter_pcu_left_placeholder' }),
        customRangeRightPlaceholder: formatMessage({ id: 'dbb_filter_pcu_right_placeholder' }),
        title: formatMessage({ id: 'dbb_filter_pcu_title' }),
        default: {
          radioSelectedValue: 'all',
          inputValue: {
            min: '',
            max: '',
          }
        },
        view_type: 'range',
        visible: 1,
        allRadioContent,
        radioListContent,
        tooltip: formatMessage({ id: 'dbb_filter_pcu_tooltip' }),
      },
      'revenue': {
        dataKey: 'revenue',
        key: 'revenue',
        nameEN: 'Revenue Range',
        nameCN: '收入区间',
        name: formatMessage({ id: 'dbb_filter_revenue_title' }),
        customRangeUnitText: '$',
        customRangeLeftPlaceholder: formatMessage({ id: 'dbb_filter_revenue_left_placeholder' }),
        customRangeRightPlaceholder: formatMessage({ id: 'dbb_filter_revenue_right_placeholder' }),
        title: formatMessage({ id: 'dbb_filter_revenue_title' }),
        default: {
          radioSelectedValue: 'all',
          inputValue: {
            min: '',
            max: '',
          }
        },
        view_type: 'range',
        visible: 1,
        allRadioContent,
        radioListContent,
        tooltip: formatMessage({ id: 'dbb_filter_revenue_tooltip' }),
      },
      'download': {
        dataKey: 'download',
        key: 'download',
        nameEN: 'Download Range',
        nameCN: '下载量区间',
        name: formatMessage({ id: 'dbb_filter_download_title' }),
        customRangeUnitText: '-',
        customRangeLeftPlaceholder: formatMessage({ id: 'dbb_filter_download_left_placeholder' }),
        customRangeRightPlaceholder: formatMessage({ id: 'dbb_filter_download_right_placeholder' }),
        title: formatMessage({ id: 'dbb_filter_download_title' }),
        default: {
          radioSelectedValue: 'all',
          inputValue: {
            min: '',
            max: '',
          }
        },
        view_type: 'range',
        visible: 1,
        allRadioContent,
        radioListContent: downLoadRadioListContent,
        tooltip: formatMessage({ id: 'dbb_filter_download_tooltip' }),
      },

      'f2p': {
        nameEN: 'Game Type',
        nameCN: '游戏类型',
        default: ['All'],
        view_type: 'single',
        visible: 1,
      },
      'release_time': {
        nameEN: 'Release Time',
        nameCN: '发布时间',
        view_type: 'rangeDate',
        key: "release_time",
        visible: 1,
        render: (props: any) =>
          <RangeDateFilter
            typeList={['date', 'month']}
            showTips={true}
            tipsInfo={formatMessage({ id: 'dbb_filter_release_time_tooltip' })}
            {...props}
            filterKey={props.key} />,
      },
      'steam_tag': {
        nameEN: <div className="db-filter-select-options-title-lf-text">
          Steam Tags
          <OptionItemInfo
            infoItem={{ value: "", text: "", info: 'Steam Tags come from the Steam platform, added by players.' }}
            bubbleProps={{ placement: "top", overlayClassName: "dbb-filter-steam-tag-info" }} />
        </div>,
        nameCN: <div className="db-filter-select-options-title-lf-text">
          Steam品类标签
          <OptionItemInfo
            infoItem={{ value: "", text: "", info: 'Steam Tags来自Steam平台，玩家添加的游戏标签' }}
            bubbleProps={{ placement: "top", overlayClassName: "dbb-filter-steam-tag-info" }} />
        </div>,
        default: [],
        view_type: 'multiple',
        visible: 1,
        radioOptions: [],
        className: 'dbb-filter-steam-tag',
        searchBoxProps: {
          placeholder: isEN ? 'Search any steam tags' : '搜索任何Steam标签',
        },
        button: (selectedOptions: any, { options, optionsValue }: any) => {
          const name = isEN ? 'Steam Tags' : 'Steam品类标签';
          const placeholder = isEN ? 'Select any Steam Tags' : '选择任何Steam标签';
          if (optionsValue?.length === options?.length) {
            return <div className={`db-filter-select-button`}>
              <span className="db-filter-select-button-text">
                {name}
              </span>
              <span className="db-filter-select-button-value">
                {isEN ? 'All' : '全部'}
              </span>
            </div>;
          }
          return <FilterSelectButton
            name={name}
            selectedOptions={selectedOptions}
            showSlectedNum={2}
            placeholder={placeholder}
          />;
        }
      }
      // 暂时未发现有地方使用，但是后端返回的有，先写一下
      // 'stage': {
      //   nameEN: 'Stage',
      //   nameCN: '步骤',
      //   default: ['all'],
      //   view_type: 'multiple',
      //   visible: 1,
      // },
    };

    // 筛选器名称映射，和后端核对后，后端暂不支持名称配置话，前端写死
    const FILTER_PC_CONSOLE_NAME_MAP = {
      'genre_subgenre': {
        nameEN: 'Genre',
        nameCN: '品类',
        // metrics: { type: '', regions: [], markets: [] },
        default: {
          genre: [],
          subgenre: ['255'],
          type: 'subgenre',
        },
        view_type: 'cascade',
        visible: 1,
        render: renderGenreSubGenre
      },
      'game': {
        nameEN: 'Game',
        nameCN: '游戏',
        default: ['255'],
        view_type: 'multiple',
        visible: 1,
        radioOptions: [{
          value: '255',
          text: formatMessage({ id: 'dbb_filter_all' }),
          info: ''
        }],
      },
      'country': {
        nameEN: 'Region/Country',
        nameCN: '区域/国家',
        default: 'global',
        view_type: 'singleCascade',
        visible: 1,
        tooltipEn: 'Please enter country',
        tooltipCn: '请输入国家',
        render: (props: any) => <CountryFilter {...props} filterKey={props.key} />,
      },
      'platform': {
        nameEN: 'Platform',
        nameCN: '平台',
        default: 'PC&Console',
        view_type: 'single',
        visible: 1,
        radioOptions: [{
          value: 'PC&Console',
          text: formatMessage({ id: 'dbb_filter_all' }),
          info: formatMessage({ id: 'dbb_filter_all_info' }, { name: isEN ? 'Platform' : '平台' }),
        }]
      },
      'pcu': {
        dataKey: 'pcu',
        key: 'pcu',
        nameEN: 'PCU Range',
        nameCN: 'PCU 区间',
        name: formatMessage({ id: 'dbb_filter_pcu_title' }),
        customRangeUnitText: '-',
        customRangeLeftPlaceholder: formatMessage({ id: 'dbb_filter_pcu_left_placeholder' }),
        customRangeRightPlaceholder: formatMessage({ id: 'dbb_filter_pcu_right_placeholder' }),
        title: formatMessage({ id: 'dbb_filter_pcu_title' }),
        default: {
          radioSelectedValue: 'all',
          inputValue: {
            min: '',
            max: '',
          }
        },
        view_type: 'range',
        visible: 1,
        allRadioContent,
        radioListContent,
        tooltip: formatMessage({ id: 'dbb_filter_pcu_tooltip' }),
      },
      'revenue': {
        dataKey: 'revenue',
        key: 'revenue',
        nameEN: 'Revenue Range',
        nameCN: '收入区间',
        name: formatMessage({ id: 'dbb_filter_revenue_title' }),
        customRangeUnitText: '$',
        customRangeLeftPlaceholder: formatMessage({ id: 'dbb_filter_revenue_left_placeholder' }),
        customRangeRightPlaceholder: formatMessage({ id: 'dbb_filter_revenue_right_placeholder' }),
        title: formatMessage({ id: 'dbb_filter_revenue_title' }),
        default: {
          radioSelectedValue: 'all',
          inputValue: {
            min: '',
            max: '',
          }
        },
        view_type: 'range',
        visible: 1,
        allRadioContent,
        radioListContent,
        tooltip: formatMessage({ id: 'dbb_filter_revenue_tooltip' }),
      },
      // 理论上只有手游会配置这个筛选器
      'download': {
        dataKey: 'download',
        key: 'download',
        nameEN: 'Download Range',
        nameCN: '下载量区间',
        name: formatMessage({ id: 'dbb_filter_download_title' }),
        customRangeUnitText: '-',
        customRangeLeftPlaceholder: formatMessage({ id: 'dbb_filter_download_left_placeholder' }),
        customRangeRightPlaceholder: formatMessage({ id: 'dbb_filter_download_right_placeholder' }),
        title: formatMessage({ id: 'dbb_filter_download_title' }),
        default: {
          radioSelectedValue: 'all',
          inputValue: {
            min: '',
            max: '',
          }
        },
        view_type: 'range',
        visible: 1,
        allRadioContent,
        radioListContent: downLoadRadioListContent,
        tooltip: formatMessage({ id: 'dbb_filter_download_tooltip' }),
      },
      'f2p': {
        nameEN: 'Game Type',
        nameCN: '游戏类型',
        default: ['All'],
        view_type: 'single',
        visible: 1,
      },
      'release_time': {
        nameEN: 'Release Time',
        nameCN: '发布时间',
        view_type: 'rangeDate',
        key: "release_time",
        visible: 1,
        render: (props: any) =>
          <RangeDateFilter
            typeList={['date', 'month']}
            showTips={true}
            tipsInfo={formatMessage({ id: 'dbb_filter_release_time_tooltip' })}
            {...props}
            filterKey={props.key} />,
      },
      'steam_tag': {
        nameEN: <div className="db-filter-select-options-title-lf-text">
          Steam Tags
          <OptionItemInfo
            infoItem={{ value: "", text: "", info: 'Steam Tags come from the Steam platform, added by players.' }}
            bubbleProps={{ placement: "top", overlayClassName: "dbb-filter-steam-tag-info" }} />
        </div>,
        nameCN: <div className="db-filter-select-options-title-lf-text">
          Steam品类标签
          <OptionItemInfo
            infoItem={{ value: "", text: "", info: 'Steam Tags来自Steam平台，玩家添加的游戏标签' }}
            bubbleProps={{ placement: "top", overlayClassName: "dbb-filter-steam-tag-info" }} />
        </div>,
        default: [],
        view_type: 'multiple',
        visible: 1,
        radioOptions: [],
        className: 'dbb-filter-steam-tag',
        searchBoxProps: {
          placeholder: isEN ? 'Search any steam tags' : '搜索任何Steam标签',
        },
        button: (selectedOptions: any, { options, optionsValue }: any) => {
          const name = isEN ? 'Steam Tags' : 'Steam品类标签';
          const placeholder = isEN ? 'Select any Steam Tags' : '选择任何Steam标签';
          if (optionsValue?.length === options?.length) {
            return <div className={`db-filter-select-button`}>
              <span className="db-filter-select-button-text">
                {name}
              </span>
              <span className="db-filter-select-button-value">
                {isEN ? 'All' : '全部'}
              </span>
            </div>;
          }
          return <FilterSelectButton
            name={name}
            selectedOptions={selectedOptions}
            showSlectedNum={2}
            placeholder={placeholder}
          />;
        }
      }
    };

    const filterConfigMap: { [key: string]: any; } = {
      pcconsole: FILTER_PC_CONSOLE_NAME_MAP,
      mobile: FILTER_MOBILE_NAME_MAP
    };
    return {
      filterConfigList: filterConfigMap[frameType as string],
    };
  }, [frameType, isEN]);

  return {
    filterConfigList,
  };
};

